import { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { store } from 'redux-rest-reducer';
import { get as getConfig } from 'actions/configs';
import { showNotification } from 'actions/notification';
import { AV_REVIEW_CONFIG } from 'constants/av-review';

import { useReviewerConfigPatch } from '../api/patch-reviewer-config';
import { MIN_WEIGHT, MAX_WEIGHT } from '../constants';
import { isWeightValid } from '../helpers';

const useReviewerConfig = () => {
  const dispatch = useDispatch();
  const configData = useSelector(({ configs }) => store.get(configs, AV_REVIEW_CONFIG));
  const sourceReviewers = configData?.data?.reviewers;
  const isLoading = Boolean(configData?.isLoading && !configData?.data?.reviewers);

  const [draftReviewers, setDraftReviewers] = useState([]);

  useEffect(() => {
    if (!sourceReviewers) return;
    setDraftReviewers(sourceReviewers.map(r => ({ ...r })));
  }, [sourceReviewers]);

  const handlePatchComplete = useCallback(
    errorMessage => {
      if (errorMessage) {
        dispatch(
          showNotification({
            text: `Failed to save reviewers: ${errorMessage}`,
            duration: 5,
            showCloseIcon: true,
          }),
        );
        return;
      }
      dispatch(getConfig(AV_REVIEW_CONFIG));
      dispatch(
        showNotification({
          text: 'Reviewer roster updated',
          duration: 4,
          showCloseIcon: true,
        }),
      );
    },
    [dispatch],
  );

  const { patch, isProcessing: isSaving } = useReviewerConfigPatch({
    onComplete: handlePatchComplete,
  });

  const updateReviewer = useCallback((id, patchObj) => {
    setDraftReviewers(prev => prev.map(r => (r.id === id ? { ...r, ...patchObj } : r)));
  }, []);

  const validationError = useMemo(() => {
    if (draftReviewers.length === 0) return null;
    if (!draftReviewers.every(r => isWeightValid(r.weight))) {
      return `Each reviewer must have a weight between ${MIN_WEIGHT} and ${MAX_WEIGHT}.`;
    }
    if (!draftReviewers.some(r => !r.on_leave)) {
      return 'At least one reviewer must not be on leave.';
    }
    return null;
  }, [draftReviewers]);

  const submit = useCallback(() => {
    if (validationError || draftReviewers.length === 0) return;
    if (JSON.stringify(sourceReviewers) === JSON.stringify(draftReviewers)) return;
    patch(undefined, { reviewers: draftReviewers });
  }, [validationError, sourceReviewers, patch, draftReviewers]);

  return {
    reviewers: draftReviewers,
    isLoading,
    isSaving,
    validationError,
    updateReviewer,
    submit,
  };
};

export default useReviewerConfig;
